(() => {
  'use strict';
  const player = document.querySelector('.xq-ambient');
  if (!player) return;
  const audio = player.querySelector('audio');
  const button = player.querySelector('button');
  const status = player.querySelector('.xq-ambient-status');
  const seek = player.querySelector('.xq-ambient-seek');
  const elapsed = player.querySelector('.xq-ambient-elapsed');
  const durationLabel = player.querySelector('.xq-ambient-duration');
  const volume = player.querySelector('.xq-ambient-volume');
  const preferenceKey = 'xeqan-background-music';
  const positionKey = 'xeqan-a-long-story-position';
  const read = (storage, key) => { try { return window[storage].getItem(key); } catch { return null; } };
  const write = (storage, key, value) => { try { window[storage].setItem(key, value); } catch {} };
  // Each page opening attempts playback, as Royal's product player does.
  let enabled = true;
  let context, gain, starting = false, requested = false, restored = false, needsFade = false;
  let fallbackFrame;
  const savedPosition = Number(read('sessionStorage', positionKey));
  let lastPositionSave = 0;
  let pendingPosition = Number.isFinite(savedPosition) && savedPosition > 0 ? savedPosition : null;
  let scrubbing = false;
  let level = 0.70;
  let fadeStarted = null;
  const fadeDuration = 6;
  let cachedSource = false;
  let cacheLoading = false;
  let resumeAfterCache = false;
  const appleTouchDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const update = state => {
    player.dataset.state = state;
    const active = state === 'playing' || state === 'starting';
    button.setAttribute('aria-pressed', String(active));
    button.setAttribute('aria-label', active ? 'Turn off background music' : 'Play background music');
    status.textContent = {playing:'Background music · On',starting:'Starting softly…',off:'Background music · Off',ready:'Tap to listen',error:'Tap to retry'}[state];
  };
  const prepare = () => {
    if (context) return;
    // Royal's native media path avoids a second autoplay gate on Android/desktop.
    // iOS needs Web Audio for gain because its media volume cannot be controlled.
    if (!appleTouchDevice) { if (audio.paused) audio.volume = pendingPosition === null ? Math.min(level, 0.02) : 0; return; }
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) { audio.volume = 0; return; }
    context = new AudioContext();
    gain = context.createGain();
    gain.gain.value = 0;
    context.createMediaElementSource(audio).connect(gain);
    gain.connect(context.destination);
  };
  const fadeIn = () => {
    fadeStarted = performance.now();
    if (gain) {
      const now = context.currentTime;
      gain.gain.cancelScheduledValues(now);
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(level, now + 6);
    } else {
      cancelAnimationFrame(fallbackFrame);
      const began = fadeStarted;
      const step = now => {
        if (audio.paused) return;
        audio.volume = level * Math.min((now - began) / 6000, 1);
        if (now - began < 6000) fallbackFrame = requestAnimationFrame(step);
      };
      fallbackFrame = requestAnimationFrame(step);
    }
  };
  volume.addEventListener('input', () => {
    level = Number(volume.value) / 100;
    volume.setAttribute('aria-valuetext', `${volume.value} percent`);
    volume.style.setProperty('--xq-volume', `${Number(volume.value) / 70 * 100}%`);
    const fraction = fadeStarted === null ? 1 : Math.min((performance.now() - fadeStarted) / (fadeDuration * 1000), 1);
    if (gain) {
      const now = context.currentTime;
      gain.gain.cancelScheduledValues(now);
      gain.gain.setValueAtTime(level * fraction, now);
      if (fraction < 1) gain.gain.linearRampToValueAtTime(level, now + fadeDuration * (1 - fraction));
    } else {
      audio.volume = level * fraction;
    }
  });
  const play = async () => {
    if (!enabled || starting || !audio.paused) return;
    starting = true;
    try {
      needsFade = true;
      await audio.play();
      if (!enabled) { audio.pause(); return; }
    } catch (error) {
      requested = false;
      update(!enabled ? 'off' : error.name === 'NotAllowedError' ? 'ready' : 'error');
    } finally { starting = false; }
  };
  const begin = manual => {
    if (!enabled) return;
    if (manual) { requested = true; update('starting'); }
    try {
      prepare();
      if (context && context.state !== 'running') {
        // Call both APIs inside the gesture; waiting for resume loses mobile activation.
        context.resume().catch(() => { requested = false; update(enabled ? 'ready' : 'off'); });
      }
      play();
    } catch { requested = false; update('error'); }
  };
  const savePosition = () => {
    const position = pendingPosition === null ? audio.currentTime : pendingPosition;
    if (restored && !scrubbing && Number.isFinite(position)) write('sessionStorage', positionKey, String(position));
  };
  const formatTime = seconds => `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;
  const updateTimeline = () => {
    const duration = audio.duration;
    const valid = Number.isFinite(duration) && duration > 0;
    seek.disabled = !valid;
    const position = pendingPosition === null ? audio.currentTime : pendingPosition;
    elapsed.textContent = formatTime(position || 0);
    durationLabel.textContent = valid ? formatTime(duration) : '—:—';
    const progress = valid ? position / duration * 100 : 0;
    if (!scrubbing) seek.value = String(progress);
    seek.style.setProperty('--xq-progress', `${progress}%`);
    seek.setAttribute('aria-valuetext', `${elapsed.textContent} of ${durationLabel.textContent}`);
  };
  // A complete local media blob avoids unreliable CDN range-seeking on mobile.
  const cacheAudioForSeeking = async () => {
    if (cachedSource || cacheLoading) return;
    cacheLoading = true;
    try {
      const response = await fetch(audio.currentSrc || audio.src, {mode:'cors', cache:'force-cache'});
      if (!response.ok) throw new Error('Audio download failed');
      const blob = await response.blob();
      const position = pendingPosition === null ? audio.currentTime : pendingPosition;
      resumeAfterCache = enabled && !audio.paused;
      pendingPosition = position;
      cachedSource = true;
      audio.src = URL.createObjectURL(blob);
      audio.load();
    } catch { /* Keep the original CDN source available if caching fails. */ }
    finally { cacheLoading = false; }
  };
  const applyPendingPosition = () => {
    if (pendingPosition === null || !Number.isFinite(audio.duration) || audio.duration <= 0) return;
    pendingPosition = Math.min(pendingPosition, Math.max(0, audio.duration - 0.05));
    if (Math.abs(audio.currentTime - pendingPosition) < 0.25) {
      pendingPosition = null;
      if (needsFade && !audio.paused) { needsFade = false; fadeIn(); }
      return;
    }
    const contains = ranges => {
      for (let i = 0; i < ranges.length; i++) {
        if (pendingPosition >= ranges.start(i) && pendingPosition <= ranges.end(i)) return true;
      }
      return false;
    };
    if (audio.seeking || (!contains(audio.seekable) && !contains(audio.buffered))) return;
    try { audio.currentTime = pendingPosition; } catch { /* Retry when media data is ready. */ }
  };
  seek.addEventListener('pointerdown', () => { scrubbing = true; });
  seek.addEventListener('touchstart', () => { scrubbing = true; }, {passive:true});
  seek.addEventListener('input', () => {
    if (!Number.isFinite(audio.duration) || audio.duration <= 0) return;
    pendingPosition = audio.duration * Number(seek.value) / 100;
    write('sessionStorage', positionKey, String(pendingPosition));
    applyPendingPosition();
    updateTimeline();
  });
  const finishScrub = () => { scrubbing = false; applyPendingPosition(); updateTimeline(); savePosition(); };
  seek.addEventListener('change', finishScrub);
  window.addEventListener('pointerup', () => { if (scrubbing) finishScrub(); }, {passive:true});
  window.addEventListener('touchend', () => { if (scrubbing) finishScrub(); }, {passive:true});
  window.addEventListener('pointercancel', () => { if (scrubbing) finishScrub(); }, {passive:true});
  audio.addEventListener('seeked', () => {
    if (pendingPosition !== null && Math.abs(audio.currentTime - pendingPosition) < 1) {
      pendingPosition = null;
      if (needsFade && !audio.paused) { needsFade = false; fadeIn(); }
    }
    updateTimeline();
    savePosition();
  });
  audio.addEventListener('progress', applyPendingPosition);
  audio.addEventListener('loadeddata', () => {
    applyPendingPosition();
    if (resumeAfterCache) { resumeAfterCache = false; if (enabled) play(); }
  });
  window.setInterval(() => { if (pendingPosition !== null) applyPendingPosition(); }, 500);
  audio.addEventListener('canplay', () => { if (pendingPosition !== null && Math.abs(audio.currentTime - pendingPosition) > 1) applyPendingPosition(); });
  audio.addEventListener('timeupdate', () => {
    updateTimeline();
    if (performance.now() - lastPositionSave >= 1000) {
      savePosition();
      lastPositionSave = performance.now();
    }
  });
  audio.addEventListener('durationchange', updateTimeline);
  const stop = () => {
    enabled = false;
    requested = false;
    write('sessionStorage', preferenceKey, 'off');
    savePosition();
    audio.pause();
    cancelAnimationFrame(fallbackFrame);
    if (gain) { gain.gain.cancelScheduledValues(context.currentTime); gain.gain.setValueAtTime(0, context.currentTime); }
    update('off');
  };
  const restorePosition = () => {
    if (restored) return;
    restored = true;
    applyPendingPosition();
    updateTimeline();
  };
  audio.addEventListener('loadedmetadata', restorePosition);
  if (audio.readyState >= 1) restorePosition();
  audio.addEventListener('playing', () => {
    if (!enabled) { audio.pause(); return; }
    requested = false;
    update('playing');
    if (needsFade && pendingPosition === null) { needsFade = false; fadeIn(); }
  });
  audio.addEventListener('pause', () => { savePosition(); if (!requested) update(enabled ? 'ready' : 'off'); });
  audio.addEventListener('error', () => { requested = false; update(enabled ? 'error' : 'off'); });
  button.addEventListener('click', () => {
    if (!audio.paused || requested) stop();
    else { enabled = true; write('sessionStorage', preferenceKey, 'on'); begin(true); }
  });
  const firstInteraction = event => {
    if (!enabled || event.target.closest('.xq-ambient button, .xq-ambient input') || requested) return;
    if (!audio.paused && (!context || context.state === 'running')) return;
    if (event.type === 'keydown' && !['Enter',' ','ArrowDown'].includes(event.key)) return;
    begin(false);
  };
  document.addEventListener('click', event => {
    if (event.target.closest('a[href]')) savePosition();
  }, {capture:true});
  document.addEventListener('click', firstInteraction);
  document.addEventListener('pointerdown', firstInteraction, {passive:true, capture:true});
  document.addEventListener('touchstart', firstInteraction, {passive:true, capture:true});
  document.addEventListener('pointerup', firstInteraction, {passive:true, capture:true});
  document.addEventListener('touchend', firstInteraction, {passive:true, capture:true});
  // Scroll is a best-effort retry; it cannot grant browser autoplay permission.
  let lastScrollAttempt = -Infinity;
  const scrollInteraction = () => {
    if (!enabled || !audio.paused || starting || requested) return;
    const now = performance.now();
    if (now - lastScrollAttempt < 1000) return;
    lastScrollAttempt = now;
    begin(false);
  };
  document.addEventListener('touchmove', scrollInteraction, {passive:true, capture:true});
  window.addEventListener('scroll', scrollInteraction, {passive:true, capture:true});
  document.addEventListener('keydown', firstInteraction);
  window.addEventListener('pagehide', savePosition);
  document.addEventListener('visibilitychange', () => { if (document.hidden) savePosition(); });
  update(enabled ? 'ready' : 'off');
  cacheAudioForSeeking();
  window.setTimeout(() => { if (enabled) begin(false); }, 260);
})();
